<script setup>
import ButtonSeed from '@/design/primitives/ButtonSeed.vue'
import CoinGlyph  from '@/design/primitives/CoinGlyph.vue'
import IconClose  from '@/design/icons/IconClose.vue'
import { useCartStore } from '@/stores/cartStore'

const props = defineProps({
  id:       { type: [String, Number], required: true },
  kind:     { type: String, required: true },
  name:     { type: String, default: '' },
  desc:     { type: String, default: '' },
  price:    { type: Number, default: 0 },
  qty:      { type: Number, default: 1 },
  imageUrl: { type: String, default: '' }
})

const cart = useCartStore()

function dec()     { if (props.qty > 1) cart.setQty(props.id, props.kind, props.qty - 1) }
function inc()     { cart.setQty(props.id, props.kind, props.qty + 1) }
function discard() { cart.remove(props.id, props.kind) }

function fmt(val) {
  return Number(val).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}
</script>

<template>
  <div class="plate" role="listitem">
    <!-- Thumbnail -->
    <div class="plate__thumb" aria-hidden="true">
      <img v-if="imageUrl" :src="imageUrl" :alt="name" class="plate__img" loading="lazy" />
      <div v-else class="plate__img-blank" />
    </div>

    <!-- Title + desc + stepper -->
    <div class="plate__body">
      <p class="plate__name u-display u-allcaps">{{ name }}</p>
      <p v-if="desc" class="plate__desc">{{ desc }}</p>

      <!-- Qty stepper -->
      <div class="plate__stepper" role="group" :aria-label="`Quantity for ${name}`">
        <ButtonSeed
          variant="ghost"
          size="sm"
          class="plate__step"
          :disabled="qty <= 1"
          aria-label="Decrease quantity"
          @click="dec"
        >−</ButtonSeed>

        <span class="plate__qty u-mono u-gold" aria-live="polite">{{ qty }}</span>

        <ButtonSeed
          variant="ghost"
          size="sm"
          class="plate__step"
          aria-label="Increase quantity"
          @click="inc"
        >+</ButtonSeed>
      </div>
    </div>

    <!-- Pricing + remove -->
    <div class="plate__pricing">
      <span class="plate__unit">
        <CoinGlyph :size="12" />
        <span class="plate__mono plate__mono--soft">{{ fmt(price) }}</span>
      </span>
      <span class="plate__total">
        <CoinGlyph :size="16" />
        <span class="plate__mono plate__mono--gold">{{ fmt(price * qty) }}</span>
      </span>
      <button class="plate__remove" aria-label="Remove item" @click="discard">
        <IconClose class="plate__remove-icon" />
      </button>
    </div>
  </div>
</template>

<style scoped>
.plate {
  display: flex;
  align-items: center;
  gap: var(--sp-4);
  padding: var(--sp-4) var(--sp-6);
  animation: rise-in var(--t-slow) var(--ease-cinema) both;
}

/* Thumbnail */
.plate__thumb {
  flex-shrink: 0;
  width: 80px;
  height: 80px;
  border-radius: var(--r-sm);
  overflow: hidden;
  border: 1px solid var(--ash);
  background: var(--bg-deep);
}
.plate__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.plate__img-blank {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, var(--bg-deep), var(--ash) 80%, var(--bg-deep));
  opacity: 0.6;
}

/* Body */
.plate__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
}
.plate__name {
  margin: 0;
  font-size: var(--fs-14);
  color: var(--ink);
  line-height: var(--lh-snug);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.plate__desc {
  margin: 0;
  font-size: var(--fs-12);
  color: var(--ink-soft);
  line-height: var(--lh-body);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Stepper */
.plate__stepper {
  display: inline-flex;
  align-items: center;
  gap: var(--sp-2);
}
.plate__step {
  /* Override ButtonSeed sm padding for compact stepper */
  padding: 4px 10px !important;
  min-width: 28px;
  border-color: var(--gold) !important;
  color: var(--gold) !important;
}
.plate__step:hover:not(:disabled) {
  background: var(--gold-soft) !important;
}
.plate__qty {
  font-size: var(--fs-16);
  min-width: 24px;
  text-align: center;
}

/* Pricing */
.plate__pricing {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--sp-1);
}
.plate__unit {
  display: inline-flex;
  align-items: center;
  gap: 3px;
}
.plate__total {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.plate__mono {
  font-family: var(--font-mono);
}
.plate__mono--soft {
  font-size: var(--fs-12);
  color: var(--ink-soft);
}
.plate__mono--gold {
  font-size: var(--fs-16);
  color: var(--gold);
  font-weight: 500;
}

/* Remove */
.plate__remove {
  margin-top: var(--sp-2);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: var(--r-xs);
  color: var(--ink-mute);
  border: 1px solid transparent;
  cursor: pointer;
  transition: color var(--t-fast) var(--ease-road),
              border-color var(--t-fast) var(--ease-road);
}
.plate__remove:hover {
  color: var(--ember);
  border-color: var(--ember);
}
.plate__remove-icon {
  width: 12px;
  height: 12px;
}

@media (max-width: 480px) {
  .plate {
    gap: var(--sp-3);
    padding: var(--sp-4);
  }
  .plate__thumb {
    width: 60px;
    height: 60px;
  }
  .plate__name { font-size: var(--fs-12); }
}
</style>
